import { createContext } from 'preact';
import { useState, useContext } from 'preact/hooks';

export const TableContext = createContext();

export const TableProvider = ({ children, tableDataDefault, tableActionsDefault }) => {

  const [tableData, setTableData] = useState(tableDataDefault);
  const [tableDataDisplay, setTableDataDisplay] = useState(tableDataDefault);
  const [tableActions, setTableActions] = useState(tableActionsDefault);

  const handleSorting = (id, data) => {
    if (typeof data == 'undefined') return [];
    if (typeof tableActions[id] === 'undefined' || (
      tableActions[id].sortedField == tableActionsDefault[id].sortedField &&
      tableActions[id].order == tableActionsDefault[id].order)) return data;
    let dataSorted;
    const sortedField = tableActions[id] ? tableActions[id].sortedField : '';
    const sortOrder = tableActions[id] ? tableActions[id].order : '';
    if (typeof data == 'undefined')
      dataSorted = [...tableData[id]];
    else
      dataSorted = data;
    if (sortedField) {
      dataSorted.sort((a, b) => {
        if (a[sortedField] === null || typeof a[sortedField] == 'undefined') return 1;
        if (b[sortedField] === null || typeof b[sortedField] == 'undefined') return -1;
        if ((a[sortedField] === null && b[sortedField]) || (typeof a[sortedField] == 'undefined' && typeof b[sortedField] == 'undefined')) return 0;
        return (
          a[sortedField].toString().localeCompare(b[sortedField].toString(), "en", {
            numeric: true,
          }) * (sortOrder === "asc" ? 1 : -1)
        );
      });
      setTableDataDisplay({
        ...tableDataDisplay,
        [id]: dataSorted
      });
    }
    return dataSorted;
  };

  const getFieldValue = (key, row) => {
    let fieldValue = null;
    if (key.includes('.')) {
      let keys = key.split('.');
      if (row[keys[0]] && row[keys[1]]) fieldValue = row[keys[0]][keys[1]];
      else fieldValue = null;
    } else {
      fieldValue = row[key];
      if (key.includes('@text-end')) {
        fieldValue = row[key.replace('@text-end', '')];
      }
    }
    return fieldValue;
  }
  const handleFiltering = (id, data) => {
    if (typeof data == 'undefined') return [];
    if (typeof tableActions[id] === 'undefined' || tableActions[id].filteredFields.length == 0) return data;
    let dataFiltered = [...data];
    const filteredFields = tableActions[id].filteredFields;
    const filteredFieldsKeys = filteredFields ? Object.keys(tableActions[id].filteredFields) : [];
    tableActions[id].filteredFields.filtered = true;
    filteredFieldsKeys.map((key) => {
      if (filteredFields[key] && key != "filtered") {
        dataFiltered = dataFiltered.filter(
          row => {
            let fieldValue = getFieldValue(key, row);
            if (fieldValue && typeof fieldValue == 'object') {
              return fieldValue.some(item => {
                return item.id == filteredFields[key];
              });
            } else if (filteredFields[key].includes('--')) {
              let between = filteredFields[key].split('--');
              return (fieldValue >= between[0] && fieldValue < between[1]);
            } else if (key.includes('@text-end')) {
              if (fieldValue) {
                const regex = new RegExp(filteredFields[key].replace("?", "\\w") + "$", "i");
                return !!fieldValue.match(regex);
              }
              return false;
            }
            return fieldValue == filteredFields[key];
          }
        )
      }
    });
    setTableDataDisplay({
      ...tableDataDisplay,
      [id]: dataFiltered
    });
    return dataFiltered;
  }

  const handleFilteringAndSorting = (id, data) => {
    return handleSorting(id, handleFiltering(id, data));
  }

  const handlePaging = (id, data) => {
    if (typeof tableActions[id] == 'undefined') return data;
    const dataPaged = data.slice((tableActions[id].pager.page - 1) * tableActions[id].pager.itemsPerPage, tableActions[id].pager.page * tableActions[id].pager.itemsPerPage);
    setTableDataDisplay({
      ...tableDataDisplay,
      [id]: dataPaged,
      nbResults: {
        ...tableDataDisplay.nbResults,
        [id]: data.length
      }
    });
    return dataPaged;
  }

  const handleDisplaying = (id) => {
    return handlePaging(id, handleFilteringAndSorting(id, tableData[id]));
  }

  function setFilter(id, field, value) {
    tableActions[id].filteredFields[field] = value;
  }

  function cancelFilters(id) {
    tableActions[id].filteredFields = { filtered: false };
    setTableActions({
      ...tableActions,
      [id]: {
        ...tableActions[id],
        pager: {
          ...tableActions[id].pager,
          page: 1
        }
      }
    })
  }

  return (
    <TableContext.Provider value={{
      tableData,
      setTableData,
      tableDataDisplay,
      setTableDataDisplay,
      tableActions,
      setTableActions,
      handleSorting,
      handleFiltering,
      handleFilteringAndSorting,
      handlePaging,
      handleDisplaying,
      setFilter,
      cancelFilters
    }}>
      {children}
    </TableContext.Provider>
  );
};

const useTable = () => {
  const context = useContext(TableContext);
  if (context === undefined) {
    throw new Error("useTable must be used within a TableProvider");
  }
  return context;
};

export default useTable;